/**
 * AuthManager - Gestor centralizado de autenticación
 * 
 * Este módulo maneja la lógica de autenticación de forma centralizada,
 * permitiendo que los interceptores de Axios puedan desloguear usuarios
 * sin depender directamente del contexto de React.
 * 
 * Principios SOLID aplicados:
 * - Single Responsibility: Solo maneja autenticación
 * - Open/Closed: Extensible mediante callbacks
 * - Dependency Inversion: Los componentes dependen de abstracciones
 */

class AuthManager {
  constructor() {
    this.logoutCallbacks = [];
    this.isLoggingOut = false;
    this.publicPaths = ['/agendar', '/portal', '/login'];
  }

  /**
   * Registra un callback que se ejecutará cuando se requiera logout
   * @param {Function} callback - Función que limpia el estado del usuario
   */
  registerLogoutCallback(callback) {
    if (typeof callback === 'function') {
      this.logoutCallbacks.push(callback);
    }
  }

  /**
   * Elimina un callback registrado
   * @param {Function} callback - Callback a eliminar
   */
  unregisterLogoutCallback(callback) {
    this.logoutCallbacks = this.logoutCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Limpia todos los tokens del localStorage
   */
  clearTokens() {
    localStorage.removeItem('reserveo_token');
    localStorage.removeItem('reserveo_user');
    // También limpiar token de paciente si existe
    localStorage.removeItem('patient_token');
  }

  /**
   * Verifica si la ruta actual es pública
   * @param {string} pathname - Ruta actual
   * @returns {boolean}
   */
  isPublicPath(pathname) {
    return this.publicPaths.some(path => pathname.startsWith(path));
  }

  /**
   * Verifica si ya estamos en la página de login
   * @param {string} pathname - Ruta actual
   * @returns {boolean}
   */
  isLoginPage(pathname) {
    return pathname === '/login' || pathname.startsWith('/login');
  }

  /**
   * Ejecuta el logout completo:
   * 1. Limpia tokens
   * 2. Ejecuta callbacks registrados
   * 3. Redirige a login (si no está ya ahí)
   * 
   * @param {Object} options - Opciones de logout
   * @param {boolean} options.redirect - Si debe redirigir (default: true)
   * @param {string} options.redirectTo - Ruta de redirección (default: '/login')
   * @param {boolean} options.force - Forzar logout incluso si ya está en proceso (default: false)
   */
  logout(options = {}) {
    const {
      redirect = true,
      redirectTo = '/login',
      force = false,
    } = options;

    // Evitar loops infinitos
    if (this.isLoggingOut && !force) {
      console.warn('Logout ya está en proceso, ignorando solicitud duplicada');
      return;
    }

    this.isLoggingOut = true;

    try {
      // 1. Limpiar tokens
      this.clearTokens();

      // 2. Ejecutar callbacks registrados (ej: limpiar estado de React Context)
      this.logoutCallbacks.forEach(callback => {
        try {
          callback();
        } catch (error) {
          console.error('Error ejecutando callback de logout:', error);
        }
      });

      // 3. Redirigir si es necesario
      if (redirect) {
        const currentPath = window.location.pathname;
        
        // No redirigir si ya estamos en login o en una ruta pública
        if (!this.isLoginPage(currentPath) && !this.isPublicPath(currentPath)) {
          // Usar window.location.href para forzar recarga completa
          // Esto asegura que todos los estados se limpien correctamente
          window.location.href = redirectTo;
        }
      }
    } catch (error) {
      console.error('Error durante logout:', error);
    } finally {
      // Resetear flag después de un breve delay para permitir redirección
      setTimeout(() => {
        this.isLoggingOut = false;
      }, 1000);
    }
  }

  /**
   * Maneja errores de autenticación (401, 419)
   * @param {Object} error - Error de Axios
   */
  handleAuthError(error) {
    const status = error?.response?.status;
    
    if (status === 401 || status === 419) {
      const message = error?.response?.data?.message || 
                     error?.response?.data?.error?.message ||
                     'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.';
      
      // Log para debugging (solo en desarrollo)
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error de autenticación detectado:', {
          status,
          message,
          path: window.location.pathname,
        });
      }

      // Ejecutar logout
      this.logout({
        redirect: true,
        redirectTo: '/login',
      });
    }
  }
}

// Singleton instance
const authManager = new AuthManager();

export default authManager;
